import { Card, CardContent, Typography, Skeleton, Box, Grid } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { MRRBreakdown } from '../../../../types/admin';
import { formatCurrency } from '../../../../utils/formatters';

interface MRRBreakdownChartProps {
  data: MRRBreakdown;
  loading?: boolean;
}

export default function MRRBreakdownChart({ data, loading }: MRRBreakdownChartProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            MRR Breakdown
          </Typography>
          <Skeleton variant="rectangular" height={200} />
        </CardContent>
      </Card>
    );
  }

  const MRRItem = ({
    label,
    value,
    isPositive,
    isNegative,
  }: {
    label: string;
    value: number;
    isPositive?: boolean;
    isNegative?: boolean;
  }) => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {isPositive && <ArrowUpwardIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        {isNegative && <ArrowDownwardIcon sx={{ fontSize: 16, color: 'error.main' }} />}
        <Typography
          variant="h6"
          sx={{
            color: isPositive ? 'success.main' : isNegative ? 'error.main' : 'text.primary',
          }}
        >
          {formatCurrency(value)}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          MRR Breakdown
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Monthly recurring revenue movement
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="Starting MRR" value={data.starting_mrr} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="New MRR" value={data.new_mrr} isPositive />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="Expansion MRR" value={data.expansion_mrr} isPositive />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="Contraction MRR" value={data.contraction_mrr} isNegative />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="Churned MRR" value={data.churned_mrr} isNegative />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MRRItem label="Ending MRR" value={data.ending_mrr} />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Net MRR Change
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: data.ending_mrr >= data.starting_mrr ? 'success.main' : 'error.main',
              fontWeight: 600,
            }}
          >
            {data.ending_mrr >= data.starting_mrr ? '+' : ''}
            {formatCurrency(data.ending_mrr - data.starting_mrr)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
